import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, RefreshCw, BookOpen, Check, X } from 'lucide-react';
import { coursesApi, adminApi } from '../../lib/api';
import { toast } from 'sonner';

export const CoursesManagement = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    language: 'python',
    difficulty: 'beginner'
  });
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const data = await coursesApi.getAll();
      setCourses(data);
      setSelectedCourseIds([]);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await coursesApi.create(formData);
      toast.success('Course created');
      setShowDialog(false);
      setFormData({ title: '', description: '', language: 'python', difficulty: 'beginner' });
      fetchCourses();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await coursesApi.delete(courseId);
      toast.success('Course deleted');
      fetchCourses();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleCourseSelection = (courseId) => {
    setSelectedCourseIds(prev => 
      prev.includes(courseId) 
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handleBulkDelete = async (permanent = false) => {
    if (selectedCourseIds.length === 0) return;
    const action = permanent ? 'permanently delete' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${selectedCourseIds.length} courses?${permanent ? ' This cannot be undone!' : ''}`)) return;
    
    setDeleting(true);
    try {
      const result = permanent 
        ? await adminApi.hardDeleteCourses(selectedCourseIds)
        : await adminApi.bulkDeleteCourses(selectedCourseIds);
      toast.success(result.message);
      fetchCourses();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold">Courses Management</h1>
        <div className="flex items-center gap-2">
          {selectedCourseIds.length > 0 && (
            <>
              <Button 
                variant="outline" 
                onClick={() => handleBulkDelete(false)}
                disabled={deleting}
                data-testid="bulk-deactivate-courses-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Deactivate ({selectedCourseIds.length})
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleBulkDelete(true)}
                disabled={deleting}
                data-testid="bulk-delete-courses-btn"
              >
                {deleting ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" /> Delete Permanently</>
                )}
              </Button>
            </>
          )}
          <Button onClick={() => setShowDialog(true)} data-testid="add-course-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Course
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-40 rounded-lg" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((course) => (
            <div 
              key={course.course_id} 
              className={`p-4 rounded-lg bg-card border transition-colors cursor-pointer ${
                selectedCourseIds.includes(course.course_id) 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:border-primary/50'
              }`} 
              data-testid={`admin-course-${course.course_id}`}
              onClick={() => toggleCourseSelection(course.course_id)}
            >
              <div className="flex items-center justify-between mb-2">
                <Badge>{course.language ? course.language.toUpperCase() : course.category?.replace('_', ' ').toUpperCase() || 'COURSE'}</Badge>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{course.difficulty}</Badge>
                  {selectedCourseIds.includes(course.course_id) && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </div>
              </div>
              <h3 className="font-semibold mb-1">{course.title}</h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{course.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{course.chapters?.length || 7} chapters</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={(e) => { e.stopPropagation(); handleDelete(course.course_id); }} 
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Course</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Course title" data-testid="course-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Course description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['python', 'sql', 'c', 'cpp', 'java', 'html', 'php', 'bash', 'shell', 'powershell'].map(lang => (
                      <SelectItem key={lang} value={lang}>{lang.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Difficulty</Label>
                <Select value={formData.difficulty} onValueChange={(v) => setFormData({ ...formData, difficulty: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-course-btn">Create Course</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Course Requests Management
